import React, {useEffect, useState} from 'react';
import {App, Button, Empty, Modal, Table, Tag, Typography} from 'antd';
import {SaveOutlined} from '@ant-design/icons';
import {MemoryBlockItem, memoryService} from '../../services/memoryService';

const {Text} = Typography;

interface MemoryManageModalProps {
    visible: boolean;
    agentId: number;
    agentName: string;
    onClose: () => void;
}

/**
 * 智能体记忆体配置弹窗：勾选共享记忆块（与私有记忆块共同组成记忆体），保存时全量替换绑定
 */
const MemoryManageModal: React.FC<MemoryManageModalProps> = ({visible, agentId, agentName, onClose}) => {
    const {message: messageApi} = App.useApp();
    const [blocks, setBlocks] = useState<MemoryBlockItem[]>([]);
    const [boundIds, setBoundIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (visible && agentId) {
            loadAll();
        } else {
            setBlocks([]);
            setBoundIds([]);
        }
    }, [visible, agentId]);

    const loadAll = async () => {
        setLoading(true);
        try {
            // 绑定范围 = 启用的共享块；已绑定集合用于回显勾选
            const [blockList, boundIdList] = await Promise.all([
                memoryService.listBlocks(),
                memoryService.listBoundBlockIds(agentId),
            ]);
            setBlocks((blockList || []).filter(b => b.status === 1));
            setBoundIds(boundIdList || []);
        } catch {
            messageApi.error('加载共享记忆块失败');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await memoryService.bindBlocks(agentId, boundIds);
            messageApi.success('记忆体配置保存成功');
            onClose();
        } catch (e: any) {
            messageApi.error(e?.response?.data?.message || '保存失败');
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {title: 'ID', dataIndex: 'id', key: 'id', width: 70},
        {
            title: '块名称', dataIndex: 'name', key: 'name', width: 200,
            render: (name: string) => <Tag color="geekblue">{name}</Tag>,
        },
        {
            title: '描述', dataIndex: 'description', key: 'description', ellipsis: true,
            render: (desc?: string) => desc || '-',
        },
        {
            title: '创建时间', dataIndex: 'createdTime', key: 'createdTime', width: 170,
        },
    ];

    return (
        <Modal
            title={`记忆体配置 - ${agentName}`}
            open={visible}
            onCancel={onClose}
            width={800}
            footer={
                <Button type="primary" icon={<SaveOutlined/>} loading={saving} onClick={handleSave}>
                    保存配置
                </Button>
            }
        >
            <Text type="secondary" style={{display: 'block', marginBottom: 12}}>
                勾选共享记忆块后，该智能体执行时将检索私有记忆 + 所勾选块内的生效记忆；
                自动提取的记忆也会按类型路由副本到对应块。
            </Text>
            <Table
                columns={columns}
                dataSource={blocks}
                rowKey="id"
                loading={loading}
                size="small"
                rowSelection={{
                    selectedRowKeys: boundIds,
                    onChange: (keys) => setBoundIds(keys as number[]),
                }}
                pagination={{pageSize: 10, showTotal: (t) => `共 ${t} 个块`}}
                locale={{emptyText: <Empty description="暂无启用的共享记忆块" image={Empty.PRESENTED_IMAGE_SIMPLE}/>}}
            />
        </Modal>
    );
};

export default MemoryManageModal;
